import classNames from 'classnames/bind';
import { motion } from 'framer-motion';
import { graphql, useStaticQuery } from 'gatsby';
import GatsbyImage from 'gatsby-image';
import React from 'react';

import {
  IMAGE_SHOW_DURATION,
  ANIMATION_SPEED_FACTOR,
  SHOW_DELAY,
  HIDE_DURATION,
  IMAGE_DELAY,
  SUPREME_WORDS_DELAY,
  SUPREME_WORD_DURATION,
  SUPREME_LOGO_DURATION_X,
  SUPREME_LOGO_DURATION_Y,
  SHOW_EASE,
  IMAGE_SHOW_EASE,
  HIDE_EASE,
} from 'constants/hero-animation-variables';
import SupremeLogoLarge from 'images/pages/home/hero/supreme-large.inline.svg';
import SupremeLogo from 'images/pages/home/hero/supreme.inline.svg';

import imageStyles from '../image-styles.module.scss';

import styles from './slide-supreme.module.scss';

const cx = classNames.bind(styles);
const imgCx = classNames.bind(imageStyles);

const SlideSupreme = () => {
  const {
    supremeTop,
    supremeBottom,
    supremeLeft,
    supremeRight,
    // eslint-disable-next-line no-use-before-define
  } = useStaticQuery(IMAGE_QUERY);

  const supremeLogoVariants = {
    initial: {
      y: '100vh',
    },
    showSupreme: {
      y: 0,
      transition: {
        duration: ANIMATION_SPEED_FACTOR * SUPREME_LOGO_DURATION_Y,
      },
    },
    hideSupreme: {
      y: '-100vh',
      transition: {
        duration: ANIMATION_SPEED_FACTOR * SUPREME_LOGO_DURATION_Y,
      },
      transitionEnd: {
        y: '100vh',
      },
    },
  };

  const supremeLogoVariantsX = {
    initial: {
      x: '30vw',
    },
    showSupreme: {
      x: '-70vw',
      transition: {
        duration: ANIMATION_SPEED_FACTOR * SUPREME_LOGO_DURATION_X,
      },
    },
    hideSupreme: {
      x: '30vw',
      transition: {
        delay: ANIMATION_SPEED_FACTOR * SUPREME_LOGO_DURATION_Y,
        duration: 0.05,
      },
    },
  };

  const imageVariants = {
    initial: {
      y: '100vh',
    },
    appear: {
      y: '100vh',
    },
    showSupreme: (custom) => ({
      y: 0,
      transition: {
        delay: ANIMATION_SPEED_FACTOR * (IMAGE_DELAY[custom].show + SHOW_DELAY),
        duration: ANIMATION_SPEED_FACTOR * IMAGE_SHOW_DURATION,
        ease: IMAGE_SHOW_EASE,
      },
    }),
    hideSupreme: (custom) => ({
      y: '-100vh',
      transition: {
        delay: ANIMATION_SPEED_FACTOR * IMAGE_DELAY[custom].hide,
        duration: ANIMATION_SPEED_FACTOR * HIDE_DURATION,
        ease: HIDE_EASE,
      },
      transitionEnd: {
        y: '100vh',
      },
    }),
  };

  const supremeSmallVariants = {
    initial: {
      opacity: 0,
    },
    showSupreme: (custom) => ({
      opacity: 1,
      transition: {
        delay:
          (custom * SUPREME_WORD_DURATION + SUPREME_WORDS_DELAY + SHOW_DELAY) *
          ANIMATION_SPEED_FACTOR,
        duration: ANIMATION_SPEED_FACTOR * 0.05,
        ease: SHOW_EASE,
      },
    }),
    hideSupreme: (custom) => ({
      opacity: 0,
      transition: {
        delay: custom * SUPREME_WORD_DURATION * ANIMATION_SPEED_FACTOR,
        duration: ANIMATION_SPEED_FACTOR * 0.05,
        ease: HIDE_EASE,
      },
    }),
  };

  return (
    <>
      <motion.div variants={imageVariants} className={imgCx('img-left')} custom="left">
        <GatsbyImage fluid={supremeLeft.childImageSharp.fluid} fadeIn={false} />
      </motion.div>
      <motion.div variants={imageVariants} className={imgCx('img-bottom')} custom="bottom">
        <GatsbyImage fluid={supremeBottom.childImageSharp.fluid} fadeIn={false} />
      </motion.div>
      <motion.div variants={imageVariants} className={imgCx('img-top')} custom="top">
        <GatsbyImage fluid={supremeTop.childImageSharp.fluid} fadeIn={false} />
      </motion.div>
      <motion.div variants={imageVariants} className={imgCx('img-right')} custom="right">
        <GatsbyImage fluid={supremeRight.childImageSharp.fluid} fadeIn={false} />
      </motion.div>
      <div className={cx('supreme-small')}>
        <motion.div variants={supremeSmallVariants} custom={2}>
          <SupremeLogo />
        </motion.div>
        <motion.div variants={supremeSmallVariants} custom={1}>
          <SupremeLogo />
        </motion.div>
        <motion.div variants={supremeSmallVariants} custom={0}>
          <SupremeLogo />
        </motion.div>
      </div>
      <motion.div className={cx('supreme-logo')} variants={supremeLogoVariants}>
        <motion.div variants={supremeLogoVariantsX}>
          <SupremeLogoLarge />
        </motion.div>
      </motion.div>
    </>
  );
};

export default SlideSupreme;

const IMAGE_QUERY = graphql`
  query {
    supremeLeft: file(relativePath: { eq: "pages/home/hero/supreme-left.jpg" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    supremeRight: file(relativePath: { eq: "pages/home/hero/supreme-right.jpg" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    supremeTop: file(relativePath: { eq: "pages/home/hero/supreme-top.jpg" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    supremeBottom: file(relativePath: { eq: "pages/home/hero/supreme-bottom.jpg" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
  }
`;
